import { Component, HostListener, ViewChild } from '@angular/core';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { debounceTime } from 'rxjs';
import { GradientHandle } from '../../models/layer.model';
import { ColorPickerModule } from 'ngx-color-picker';

export interface GradientObject {
  items: FormArray
}

@Component({
  selector: 'app-gradient-picker',
  standalone: true,
  imports: [
    ColorPickerModule,
    InputNumberModule,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './gradient-picker.component.html',
  styleUrl: './gradient-picker.component.scss',
  providers: []
})
export class GradientPickerComponent implements ControlValueAccessor {
  gradientForm: FormGroup<GradientObject>

  private onChange = (value: GradientHandle[]) => {}
  private onTouched = () => {}

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private ngControl: NgControl
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    this.gradientForm = this.fb.group({
      items: this.fb.array([
        this.getNewHandle('#FF0000', 0),
        this.getNewHandle('#00FF00', 100),
      ])
    })

    this.gradientForm.valueChanges
      .pipe(
        debounceTime(1000)
      )
      .subscribe(value => {
        this.items.controls.sort((a, b) => a.get('position')?.value - b.get('position')?.value)

        this.onChange(this.serializeData(value))
        this.onTouched()
      })
  }

  get items(): FormArray {
    return this.gradientForm.get('items') as FormArray
  }

  getNewHandle(color: string, position: number): FormGroup {
    return this.fb.group({
      color: [color, Validators.required],
      position: [position, [Validators.required, Validators.min(0), Validators.max(100)]]
    })
  }

  cloneClick(control: AbstractControl): void {
    this.items.push(this.getNewHandle(control.get('color')?.value, control.get('position')?.value))
  }

  writeValue(obj: GradientHandle[]): void {
    this.gradientForm = this.deserializeData(obj)
    this.gradientForm.valueChanges.subscribe((value) => {
      this.onChange(this.serializeData(value))
      this.onTouched()
    })

    this.gradientForm.valueChanges
      .pipe(
        debounceTime(1000)
      )
      .subscribe(_ => {
        this.items.controls.sort((a, b) => a.get('position')?.value - b.get('position')?.value)
      })
  }

  private deserializeData(data: GradientHandle[]): FormGroup<GradientObject> {
    return this.fb.group({
      items: this.fb.array(data.map(item => this.getNewHandle(item.color, item.position * 100)))
    })
  }


  private serializeData(data: Partial<{items: GradientHandle[]}>): GradientHandle[] {
    if (!data.items) {
      return []
    }
    return data.items.map(item => ({color: item.color, position: item.position / 100})).sort((a, b) => a.position - b.position)
  }

  registerOnChange(fn: any): void {
      this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  deleteClick(control: AbstractControl) {
    if (this.items.length === 1) {
      this.messageService.add({severity: 'error', summary: 'Invalid Action', detail: `Gradient must have at least one handle`});
      return
    }
    const index = this.items.controls.indexOf(control)
    this.items.removeAt(index)
  }

  updateColor(control: AbstractControl, color: string) {
    control.patchValue({
      color
    })
  }
}
